import { ChangeDetectionStrategy, Component, OnDestroy, DestroyRef, inject, signal, input, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EditSelectable } from '../edit-select-form-control/edit-select-form-control.component';
import { FormControl, FormGroup } from '@angular/forms';
import { LinkableService } from '../../services/entity-link/linkable/linkable.service';
import { LinkableFilter } from '../../entities/entityLink/linkableFilter';
import { Linkable } from '../../entities/entityLink/linkable';
import { ApolloService } from '../../services/apollo.service';
import { GET_LINKED_ENTITIES_QUERY, getLinkedEntitiesResponse } from '../single-entity-link-widget/graphql';
import { catchError, map, take } from 'rxjs/operators';
import { EntityLink } from '../../entities/entityLink/entityLink';
import { LinkedEntityConnection } from '../../entities/entityLink/linkedEntityConnection';
import { forkJoin, of } from 'rxjs';
import { IssueWidget } from '../../services/issue/issue-widget.interface';
import { WidgetCoordinatorService } from '../../services/issue/widget-coordinator.service';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { Reloadable } from '../../services/command/command.service';

@Component({
  selector: 'nx-multi-entity-link-widget',
  templateUrl: './multi-entity-link-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiEntityLinkWidgetComponent implements OnDestroy, IssueWidget, Reloadable {

  entity = input.required<Linkable>();
  linkedEntityType = input.required<string>();
  linkType = input.required<string>();
  label = input.required<string>();
  placeholder = input.required<string>();

  editMode = signal(false);
  isLoading = signal(false);
  linkedEntityConnections = signal<LinkedEntityConnection[]>([]);
  options = signal<EditSelectable[]>([]);

  selectedControl = new FormControl<string[]>([]);
  form = new FormGroup({
    selected: this.selectedControl
  });

  private linkableService = inject(LinkableService);
  private apolloService = inject(ApolloService);
  private coordinator = inject(WidgetCoordinatorService);
  private destroyRef = inject(DestroyRef);
  private refetchCoordinator = inject(RefetchCoordinator);

  private widgetId = crypto.randomUUID();
  private unregisterRefetch?: () => void;

  constructor() {
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          this.cancel();
        }
      });

    effect(() => {
      const currentEntity = this.entity();
      if (currentEntity?.id && currentEntity?.entityType) {
        this.loadLinks();
        this.unregisterRefetch?.();
        this.unregisterRefetch = this.refetchCoordinator.register(
          this,
          currentEntity.entityType,
          currentEntity.id
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  reload(): void {
    this.loadLinks();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    this.editMode.set(true);
    this.selectedControl.setValue(this.linkedEntityConnections().map(c => c.linkedEntity.id));
    this.loadLinkables('');
  }

  save() {
    this.editMode.set(false);
    const newIds = this.selectedControl.value ?? [];
    const currentConnections = this.linkedEntityConnections();
    const currentIds = currentConnections.map(c => c.linkedEntity.id);

    const toUnlink = currentConnections.filter(c => !newIds.includes(c.linkedEntity.id));
    const toLink = newIds.filter(id => !currentIds.includes(id));

    if (toUnlink.length === 0 && toLink.length === 0) return;

    this.isLoading.set(true);
    const entity = this.entity();

    const unlinkOps = toUnlink.map(c =>
      this.linkableService.unlinkEntities(c.linkId).pipe(catchError(() => of(null)))
    );

    const linkOps = toLink.map(id => {
      const entityLinkInput: EntityLink = {
        outwardEntityId: entity.id,
        outwardEntityType: entity.entityType,
        inwardEntityId: id,
        inwardEntityType: this.linkedEntityType(),
        linkType: this.linkType()
      };
      return this.linkableService.linkEntities(entityLinkInput).pipe(catchError(() => of(null)));
    });

    forkJoin([...unlinkOps, ...linkOps]).pipe(take(1)).subscribe(() => {
      this.loadLinks();
      this.refetchCoordinator.emit({ entityType: entity.entityType, entityId: entity.id });
    });
  }

  cancel() {
    this.editMode.set(false);
  }

  buildEntityDetailsUrl(connection: LinkedEntityConnection): string {
    if (connection?.linkedEntity?.entityType === 'Contact') {
      return `/customers/customer/${connection.linkedEntity.id}`;
    }
    if (connection?.linkedEntity?.entityType === 'Resource') {
      return `/resources/resource/${connection.linkedEntity.id}`;
    }
    if (connection?.linkedEntity?.entityType === 'Order') {
      return `/orders/order/${connection.linkedEntity.id}`;
    }
    return '';
  }

  private loadLinkables(filterText: string): void {
    const linkableFilter: LinkableFilter = {
      entityTypes: [this.linkedEntityType()],
      text: filterText
    };

    this.linkableService.getLinkables(linkableFilter)
      .pipe(
        map(linkables => linkables.map(linkable => ({
          id: linkable.id,
          name: linkable.text
        }))),
        catchError(() => of([]))
      )
      .subscribe(options => {
        this.options.set(options);
      });
  }

  private loadLinks(): void {
    const entity = this.entity();
    if (!entity?.id || !this.linkedEntityType()) return;

    this.apolloService.query<getLinkedEntitiesResponse>(GET_LINKED_ENTITIES_QUERY, {
      entityId: entity.id,
      linkedEntityType: this.linkedEntityType(),
      linkType: this.linkType(),
      pagination: { number: 0, size: 50 },
      sortRequest: { sortProperties: ['id'], sortDirection: 'ASC' }
    })
      .pipe(
        take(1),
        map(result =>
          result.data.getLinkedEntities.content.map(item => new LinkedEntityConnection(item))
        )
      )
      .subscribe({
        next: connections => {
          this.linkedEntityConnections.set(connections);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
  }
}
